import React, { useState } from "react";
import "./BatchDetails.css";
import "./TrainingBatchStatus.css";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DataTable from "./DataTable";
import FacultyViewer from "../Components/Viewers/FacultyViewer";
import { batchStatusAPI, facultyAPI } from "./api";

function TrainingBatchStatus() {
  const [batchType, setBatchType] = useState("quality");
  const [batchFrom, setBatchFrom] = useState("");
  const [batchTo, setBatchTo] = useState("");
  const [faculty, setFaculty] = useState("");
  const [facultyCode, setFacultyCode] = useState("");
  const [showFacViewer, setShowFacViewer] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromDateOpen, setFromDateOpen] = useState(false);
  const [toDateOpen, setToDateOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [facultyMap, setFacultyMap] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const resolveFacultyNames = (row) =>
    [row.faculty_code, row.faculty_code_1, row.faculty_code_2, row.faculty_code_3, row.faculty_code_4]
      .filter(Boolean)
      .map(c => facultyMap[String(c)] || String(c))
      .join(', ');

  const columns = [
    { header: "Batch No", accessor: (row) => row.batch_sr_no ?? "" },
    { header: "Batch Size", accessor: (row) => row.batch_size ?? "" },
    { header: "Start Date", accessor: (row) => row.start_date.split("T")[0] ?? "" },
    { header: "End Date", accessor: (row) => row.end_date.split("T")[0] ?? "" },
    {
      header: "No. of Days", accessor: (row) => row.no_days ?? ""
    },
    { header: "Program Title", accessor: (row) => row.title ?? "" },
    { header: "Faculty Name", accessor: (row) => resolveFacultyNames(row) },
    { header: "Venue", accessor: (row) => row.venue ?? "" },
    { header: "City", accessor: (row) => row.trng_city ?? "" },
    { header: "Location", accessor: (row) => row.trng_location ?? "" },
    { header: "Batch Status", accessor: (row) => row.trng_status ?? "" },
    { header: "No of Participants", accessor: (row) => row.no_of_participants ?? "" },
    { header: "Training Man Days", accessor: (row) => row.trng_man_days ?? "" },
    { header: "Faculty Man days", accessor: (row) =>  row.faculty_man_days ?? "" },
  ];

  const handleFacultySelect = (row) => {
    setFaculty(row.faculty_name ?? "");
    setFacultyCode(row.faculty_code ?? "");
    setShowFacViewer(false);
  };

  const handleFacultyClear = () => {
    setFaculty("");
    setFacultyCode("");
  };

  const handleView = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = {};
      if (batchFrom) params.batch_sr_no_from = batchFrom;
      if (batchTo) params.batch_sr_no_to = batchTo;
      if (fromDate) params.start_date = fromDate.toLocaleDateString('en-CA');
      if (toDate) params.end_date = toDate.toLocaleDateString('en-CA');
      if (facultyCode) params.faculty_code = facultyCode;
      params.batch_type = batchType === "freelance" ? "FREE" : "DEFA";
      
      const [batchData, facultyData] = await Promise.all([
        batchStatusAPI.getWithParticipants(params),
        facultyAPI.getAll()
      ]);
      
      // Build faculty map
      const fMap = {};
      if (Array.isArray(facultyData)) {
        for (const f of facultyData) {
          fMap[String(f.faculty_code)] = f.faculty_name;
        }
      }
      setFacultyMap(fMap);
      
      setTableData(Array.isArray(batchData) ? batchData : []);
      setShowTable(true);
    } catch {
      setErrorMsg("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBatchType("quality");
    setBatchFrom("");
    setBatchTo("");
    setFaculty("");
    setFacultyCode("");
    setFromDate(null);
    setToDate(null);
    setTableData([]);
    setFacultyMap({});
    setShowTable(false);
    setErrorMsg("");
  };

  const handleExport = () => {
    const today = new Date().toLocaleDateString("en-GB");
    const headers = [
      "Batch No", "Batch Size", "Start Date", "End Date", "No. of Days",
      "Program Title", "Faculty Name", "Venue", "City", "Location",
      "Batch Status", "No of Participants", "Training Man Days", "Faculty Man days"
    ];
    const data = tableData.map((row) => [
      row.batch_sr_no ?? "",
      row.batch_size ?? "",
      row.start_date ? row.start_date.split("T")[0] : "",
      row.end_date ? row.end_date.split("T")[0] : "",
      row.start_date && row.end_date ?
        Math.floor((new Date(row.end_date).getTime() - new Date(row.start_date).getTime()) / (1000 * 60 * 60 * 24)) + 1 : "",
      row.title ?? "",
      resolveFacultyNames(row),
      row.venue ?? "",
      row.trng_city ?? "",
      row.trng_location ?? "",
      row.trng_status ?? "",
      row.no_of_participants ?? "",
      row.trng_man_days ?? "",
      row.faculty_man_days ?? ""
    ]);
    const sum = (field) => tableData.reduce((a, r) => a + (Number(r[field]) || 0), 0);
    const totalRow = [
      "TOTAL", sum('batch_size'), "", "", "",
      "", "", "", "", "", "",
      sum('no_of_participants'), sum('trng_man_days'), sum('faculty_man_days')
    ];
    const aoa = [
      ["Report Title : Training Batch Status"],
      [`Report Date : ${today}`],
      [],
      headers,
      ...data,
      totalRow,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Training Batch Status");
    XLSX.writeFile(wb, "TrainingBatchStatus.xlsx");
  };

  // const totalIdentified = tableData.reduce((s, r) => s + (Number(r.trng_needs_identified) || 0), 0);
  // const totalMet = tableData.reduce((s, r) => s + (Number(r.trng_needs_met) || 0), 0);
  // const totalYet = tableData.reduce((s, r) => s + (Number(r.yet_to_be_met) || 0), 0);

  return (
    <div className="bd-container">

      {/* FILTERS */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }} className="no-print">
        <table style={{ fontFamily: "GEG-Body", fontSize: 12.5, borderSpacing: "0 8px", borderCollapse: "separate" }}>
          <tbody>
            <tr>
              <td className="bd-label" style={{ paddingRight: 12, paddingBottom: 4 }}>Batch Type</td>
              <td colSpan={3} style={{ paddingBottom: 4 }}>
                <label style={{ marginRight: 24, display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <input type="radio" name="batchType" value="quality" checked={batchType === "quality"} onChange={() => setBatchType("quality")} />
                  Godrej Training Batch
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <input type="radio" name="batchType" value="freelance" checked={batchType === "freelance"} onChange={() => setBatchType("freelance")} />
                  Freelance Training Batch
                </label>
              </td>
            </tr>
            <tr>
              <td className="bd-label" style={{ paddingRight: 12 }}>Batch No : From</td>
              <td style={{ paddingRight: 48 }}>
                <input className="bd-input" type="number" value={batchFrom} onChange={(e) => setBatchFrom(e.target.value)} />
              </td>
              <td className="bd-label" style={{ paddingRight: 12 }}>Batch No : To</td>
              <td>
                <input className="bd-input" type="number" value={batchTo} onChange={(e) => setBatchTo(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="bd-label" style={{ paddingRight: 12 }}>Faculty</td>
              <td style={{ paddingRight: 48 }}>
                <input className="bd-input" type="text" value={faculty} readOnly onFocus={(e) => e.target.blur()} />
                <button className="btn btn-outline" type="button" onClick={() => setShowFacViewer(true)}>Get</button>
                <button className="btn btn-secondary" type="button" onClick={handleFacultyClear}>Clear</button>
              </td>
              <td className="bd-label" style={{ paddingRight: 12 }}>Start Date (DD/MM/YY)</td>
              <td>
                <div className="tbs-date-wrap" onClick={() => setFromDateOpen(true)}>
                  <DatePicker
                    open={fromDateOpen}
                    onClickOutside={() => setFromDateOpen(false)}
                    selected={fromDate}
                    onChange={(date) => { setFromDate(date); setFromDateOpen(false); }}
                    dateFormat="dd/MM/yyyy"
                    className="tbs-date-input"
                    placeholderText="Select start date"
                    showIcon
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={<input readOnly />}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td className="bd-label" style={{ paddingRight: 12 }}>End Date (DD/MM/YY)</td>
              <td colSpan={3}>
                <div className="tbs-date-wrap" onClick={() => setToDateOpen(true)}>
                  <DatePicker
                    open={toDateOpen}
                    onClickOutside={() => setToDateOpen(false)}
                    selected={toDate}
                    onChange={(date) => { setToDate(date); setToDateOpen(false); }}
                    dateFormat="dd/MM/yyyy"
                    className="tbs-date-input"
                    placeholderText="Select end date"
                    minDate={fromDate}
                    showIcon
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={<input readOnly />}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ACTION ROW */}
      <div className="action-row no-print">
        <button className="btn btn-outline" onClick={handleReset}>Reset</button>
        <button className="btn btn-outline" onClick={handleView} disabled={loading}>
          {loading ? "Loading…" : "View"}
        </button>
      </div>

      {errorMsg && (
        <p style={{ textAlign: "center", fontSize: 11, fontFamily: "GEG-Body", color: "red", margin: "4px 0" }}>{errorMsg}</p>
      )}

      {/* TABLE AREA */}
      {showTable && (
        <div className="bd-details">
          <DataTable
            columns={columns}
            data={tableData}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => { }}
            onChangeEditCell={() => { }}
            selectedForDelete={{}}
            onToggleDeleteRow={() => { }}
            emptyMessage="No data found"
            rowKey={(_row, idx) => idx}
            footerRow={tableData.length > 0 ? [
              "Total",
              tableData.reduce((sum, row) => sum + (Number(row.batch_size) || 0), 0),
              "", "", "",
              "", "", "", "", "", "",
              tableData.reduce((sum, row) => sum + (Number(row.no_of_participants) || 0), 0),
              tableData.reduce((sum, row) => sum + (Number(row.trng_man_days) || 0), 0),
              tableData.reduce((sum, row) => sum + (Number(row.faculty_man_days) || 0), 0),
            ] : null}
          />
          {tableData.length > 0 && (
            <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
              <button className="btn btn-outline" onClick={handleExport}>Excel Export</button>
            </div>
          )}
        </div>
      )}

      {/* Faculty Viewer Modal */}
      {showFacViewer && (
        <div className="facviewer-overlay" onClick={() => setShowFacViewer(false)}>
          <div className="facviewer-modal" onClick={(e) => e.stopPropagation()}>
            <button className="facviewer-close" onClick={() => setShowFacViewer(false)}>&times;</button>
            <FacultyViewer selectable onSelect={handleFacultySelect} />
          </div>
        </div>
      )}
    </div>
  );
}

export default TrainingBatchStatus;